import React, { useState, useEffect, useMemo, useRef } from 'react';
import { documentsApi, dealsApi, organizationsApi } from '../services/api';
import { 
  Library as LibraryIcon, 
  Settings, 
  FileText, 
  Tag, 
  Calendar, 
  Circle, 
  Trash2,
  Search,
  Filter,
  CheckSquare,
  Square,
  FolderOpen,
  X,
  Plus,
  Loader2,
  AlertCircle,
  CheckCircle2,
  MoreHorizontal,
  Link as LinkIcon,
  Unlink,
  Upload
} from 'lucide-react';
import './Library.css';



const defaultCategories = [
  'Financial', 'Legal', 'Operational', 'HR', 'Insurance',
  'Facilities', 'IT', 'Sales', 'Procurement', 'General'
];

const SLB_PROPERTY_CATEGORIES = [
  { id: 'alta_title', label: 'ALTA Survey / Title Commitment' },
  { id: 'phase1_esa', label: 'Phase I Environmental Site Assessment' },
  { id: 'pca', label: 'Property Condition Assessment' },
  { id: 'building_specs', label: 'Building Specifications' },
  { id: 'zoning', label: 'Zoning and Use Permits' },
  { id: 'market_rent', label: 'Market Rent Analysis' },
  { id: 'property_tax', label: 'Property Tax Records' },
  { id: 'liens', label: 'Existing Liens and Encumbrances' },
  { id: 'property_insurance', label: 'Property Insurance History' },
];

const SLB_TENANT_CATEGORIES = [
  { id: 'financial_statements', label: 'Financial Statements (3 Years)' },
  { id: 'tax_returns', label: 'Tax Returns (Federal, 3 Years)' },
  { id: 't12_pl', label: 'Trailing 12-Month P&L' },
  { id: 'pe_sponsor', label: 'PE Sponsor Information' },
  { id: 'customer_concentration', label: 'Customer Concentration Analysis' },
  { id: 'lease_confirmation', label: 'Lease Rate Confirmation Letter' },
  { id: 'key_contracts', label: 'Key Contracts (Top 3-5)' },
  { id: 'debt_schedule', label: 'Debt Schedule' },
  { id: 'industry_context', label: 'Industry / Market Context' },
];

const ALL_SLB_CATEGORIES = [...SLB_PROPERTY_CATEGORIES, ...SLB_TENANT_CATEGORIES];

function getSLBCategoryLabel(catId) {
  const found = ALL_SLB_CATEGORIES.find(c => c.id === catId);
  return found ? found.label : null;
}

const processingStatusConfig = {
  pending: { color: '#9CA3AF', icon: Circle, label: 'Pending' },
  processing: { color: '#3B82F6', icon: Loader2, label: 'Processing' },
  completed: { color: '#10B981', icon: CheckCircle2, label: 'Completed' },
  failed: { color: '#EF4444', icon: AlertCircle, label: 'Failed' }
};

function Library({ onOpenAnnotate, canEdit = true }) {
  const [documents, setDocuments] = useState([]);
  const [deals, setDeals] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dealFilter, setDealFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Bulk selection
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  
  // Modals
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showDealModal, setShowDealModal] = useState(false);
  const [showManageCategoriesModal, setShowManageCategoriesModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  
  // Bulk action state
  const [bulkActionType, setBulkActionType] = useState(null); // 'category' or 'deal'
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const uploadInputRef = useRef(null);
  const [bulkTargetValue, setBulkTargetValue] = useState('');

  // Inline category dropdown: null | { jobId, top, left }
  const [openCategoryDropdown, setOpenCategoryDropdown] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!openCategoryDropdown) return;
    const close = () => setOpenCategoryDropdown(null);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openCategoryDropdown]);

  const fetchData = async () => {
    try {
      const [docsRes, dealsRes, catsRes] = await Promise.all([
        documentsApi.list(),
        dealsApi.list(),
        organizationsApi.getCategories().catch(() => ({ data: { customCategories: [] } }))
      ]);
      const loadedDeals = dealsRes.data || [];
      const dealMap = {};
      loadedDeals.forEach(d => { dealMap[d.id] = d.name; });

      // Map Supabase fields to frontend format
      const mappedDocs = (docsRes.data.documents || []).map(doc => {
        const dId = doc.dealId || doc.deal_id;
        return {
          jobId: doc.jobId || doc.id,
          filename: doc.filename,
          category: doc.category,
          dealId: dId,
          dealName: doc.dealName || doc.deal_name || dealMap[dId] || null,
          aiProcessingStatus: doc.aiProcessingStatus || doc.ai_processing_status,
          createdAt: doc.createdAt || doc.created_at,
          categorySuggestedByAI: doc.categorySuggestedByAI || doc.category_suggested_by_ai
        };
      });
      setDocuments(mappedDocs);
      setDeals(loadedDeals);
      setCustomCategories(catsRes.data.customCategories || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const allCategories = useMemo(() => {
    return [...defaultCategories, ...ALL_SLB_CATEGORIES.map(c => c.id), ...customCategories];
  }, [customCategories]);

  const allCategoryLabels = useMemo(() => {
    return [...defaultCategories, ...customCategories];
  }, [customCategories]);

  const filteredDocuments = useMemo(() => {
    return documents.filter(doc => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = doc.filename?.toLowerCase().includes(query) ||
                            doc.category?.toLowerCase().includes(query) ||
                            doc.dealName?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Category filter
      if (categoryFilter !== 'all' && doc.category !== categoryFilter) {
        return false;
      }
      
      // Deal filter
      if (dealFilter === 'unlinked' && doc.dealId) {
        return false;
      } else if (dealFilter === 'linked' && !doc.dealId) {
        return false;
      } else if (dealFilter !== 'all' && dealFilter !== 'linked' && dealFilter !== 'unlinked' && doc.dealId !== dealFilter) {
        return false;
      }
      
      // Status filter
      if (statusFilter !== 'all' && doc.aiProcessingStatus !== statusFilter) {
        return false;
      }
      
      return true;
    });
  }, [documents, searchQuery, categoryFilter, dealFilter, statusFilter]);

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedDocs.size === filteredDocuments.length) {
      setSelectedDocs(new Set());
    } else {
      setSelectedDocs(new Set(filteredDocuments.map(d => d.jobId)));
    }
  };

  const toggleSelectDoc = (jobId) => {
    const newSelected = new Set(selectedDocs);
    if (newSelected.has(jobId)) {
      newSelected.delete(jobId);
    } else {
      newSelected.add(jobId);
    }
    setSelectedDocs(newSelected);
  };

  // Bulk actions
  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedDocs.size} documents?`)) return;
    
    try {
      await documentsApi.bulkDelete(Array.from(selectedDocs));
      setDocuments(prev => prev.filter(d => !selectedDocs.has(d.jobId)));
      setSelectedDocs(new Set());
    } catch (error) {
      console.error('Bulk delete failed:', error);
      alert('Failed to delete documents');
    }
  };

  const handleBulkCategoryUpdate = async () => {
    if (!bulkTargetValue) return;
    
    try {
      await documentsApi.bulkUpdateCategory(Array.from(selectedDocs), bulkTargetValue);
      setDocuments(prev => prev.map(d => 
        selectedDocs.has(d.jobId) ? { ...d, category: bulkTargetValue, categorySuggestedByAI: false } : d
      ));
      setSelectedDocs(new Set());
      setShowCategoryModal(false);
      setBulkTargetValue('');
    } catch (error) {
      console.error('Bulk category update failed:', error);
      alert('Failed to update categories');
    }
  };

  const handleBulkDealLink = async () => {
    const deal = deals.find(d => d.id === bulkTargetValue);
    if (!deal) return;
    
    try {
      await documentsApi.bulkLinkDeal(Array.from(selectedDocs), deal.id, deal.name);
      setDocuments(prev => prev.map(d => 
        selectedDocs.has(d.jobId) ? { ...d, dealId: deal.id, dealName: deal.name } : d
      ));
      setSelectedDocs(new Set());
      setShowDealModal(false);
      setBulkTargetValue('');
    } catch (error) {
      console.error('Bulk deal link failed:', error);
      alert('Failed to link documents to deal');
    }
  };

  // Single document actions
  const handleDeleteDocument = async (jobId, filename) => {
    if (!window.confirm(`Delete "${filename}"?`)) return;

    try {
      await documentsApi.delete(jobId);
      setDocuments(prev => prev.filter(doc => doc.jobId !== jobId));
    } catch (error) {
      console.error('Failed to delete document:', error);
      alert('Failed to delete document');
    }
  };

  const handleUpdateCategory = async (jobId, category) => {
    try {
      await documentsApi.updateCategory(jobId, category);
      setDocuments(prev => prev.map(d => 
        d.jobId === jobId ? { ...d, category, categorySuggestedByAI: false } : d
      ));
    } catch (error) {
      console.error('Failed to update category:', error);
      alert('Failed to update category');
    }
  };

  const handleLinkDeal = async (jobId, dealId) => {
    try {
      if (dealId === null) {
        await documentsApi.linkDeal(jobId, null);
        setDocuments(prev => prev.map(d => 
          d.jobId === jobId ? { ...d, dealId: null, dealName: null } : d
        ));
      } else {
        const deal = deals.find(d => d.id === dealId);
        await documentsApi.linkDeal(jobId, deal.id, deal.name);
        setDocuments(prev => prev.map(d => 
          d.jobId === jobId ? { ...d, dealId: deal.id, dealName: deal.name } : d
        ));
      }
    } catch (error) {
      console.error('Failed to link/unlink deal:', error);
      alert('Failed to update deal association');
    }
  };

  // Category management
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    
    try {
      await organizationsApi.addCategory(newCategoryName.trim());
      setCustomCategories(prev => [...prev, newCategoryName.trim()]);
      setNewCategoryName('');
    } catch (error) {
      console.error('Failed to add category:', error);
      alert(error.response?.data?.error || 'Failed to add category');
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(`Delete category "${category}"? Documents will be moved to "General".`)) return;
    
    try {
      await organizationsApi.deleteCategory(category);
      setCustomCategories(prev => prev.filter(c => c !== category));
      setDocuments(prev => prev.map(d => 
        d.category === category ? { ...d, category: 'General' } : d
      ));
    } catch (error) {
      console.error('Failed to delete category:', error);
      alert('Failed to delete category');
    }
  };

  // Status badge component
  const ProcessingStatusBadge = ({ status }) => {
    const config = processingStatusConfig[status] || processingStatusConfig.pending;
    const Icon = config.icon;
    return (
      <span className={`processing-badge ${status}`}>
        <Icon size={12} className={status === 'processing' ? 'spinning' : ''} />
        {config.label}
      </span>
    );
  };


  if (loading) {
    return (
      <div className="library-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading documents...</p>
        </div>
      </div>
    );
  }

  const handleLibraryUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    setUploadError('');
    try {
      for (const file of files) {
        await documentsApi.upload(file);
      }
      await fetchData();
    } catch (err) {
      setUploadError('Upload failed. Please try again.');
      console.error('Library upload error:', err);
    } finally {
      setUploading(false);
      if (uploadInputRef.current) uploadInputRef.current.value = '';
    }
  };

  return (
    <div className="library-container">
      {/* Header */}
      <div className="page-header">
        <div className="page-title">
          <div className="page-title-icon"><LibraryIcon size={20} /></div>
          <h1>Document Library</h1>
          <span className="doc-count">{filteredDocuments.length} documents</span>
        </div>
        <div className="header-actions">
          {canEdit && (
            <>
              <input
                ref={uploadInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx"
                style={{ display: 'none' }}
                onChange={handleLibraryUpload}
              />
              <button
                className="btn-primary"
                onClick={() => uploadInputRef.current?.click()}
                disabled={uploading}
              >
                <Upload size={16} />
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </>
          )}
          <button
            className="btn-secondary"
            onClick={() => setShowManageCategoriesModal(true)}
          >
            <Settings size={16} /> Manage Categories
          </button>
        </div>
        {uploadError && <p style={{ color: '#EF4444', fontSize: 13, marginTop: 8 }}>{uploadError}</p>}
      </div>

      {/* Search and Filters */}
      <div className="library-toolbar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <button 
          className={`btn-filter ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter size={16} /> Filters
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="filter-panel">
          <div className="filter-group">
            <label>Category</label>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="all">All Categories</option>
              {allCategoryLabels.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
              {ALL_SLB_CATEGORIES.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.label}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Deal</label>
            <select value={dealFilter} onChange={(e) => setDealFilter(e.target.value)}>
              <option value="all">All Deals</option>
              <option value="linked">Linked to Deal</option>
              <option value="unlinked">Not Linked</option>
              {deals.map(deal => (
                <option key={deal.id} value={deal.id}>{deal.name}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>AI Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <button className="btn-clear-filters" onClick={() => {
            setCategoryFilter('all');
            setDealFilter('all');
            setStatusFilter('all');
            setSearchQuery('');
          }}>
            Clear All
          </button>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedDocs.size > 0 && (
        <div className="bulk-actions-bar">
          <div className="bulk-selection-info">
            <button className="btn-icon" onClick={() => setSelectedDocs(new Set())}>
              <X size={16} />
            </button>
            <span>{selectedDocs.size} selected</span>
          </div>
          <div className="bulk-action-buttons">
            {canEdit && <button onClick={() => { setBulkActionType('category'); setShowCategoryModal(true); }}>
              <Tag size={14} /> Change Category
            </button>}
            {canEdit && <button onClick={() => { setBulkActionType('deal'); setShowDealModal(true); }}>
              <LinkIcon size={14} /> Link to Deal
            </button>}
            {canEdit && <button className="btn-danger" onClick={handleBulkDelete}>
              <Trash2 size={14} /> Delete
            </button>}
          </div>
        </div>
      )}

      {/* Documents Table */}
      <div className="documents-table-container">
        {filteredDocuments.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><FileText size={48} /></div>
            <h3>No documents found</h3>
            <p>Upload documents or adjust your filters to see results.</p>
          </div>
        ) : (
          <table className="documents-table">
            <thead>
              <tr>
                <th className="col-checkbox">
                  <button className="btn-checkbox" onClick={toggleSelectAll}>
                    {selectedDocs.size === filteredDocuments.length ? 
                      <CheckSquare size={18} /> : <Square size={18} />
                    }
                  </button>
                </th>
                <th className="col-filename">Document</th>
                <th className="col-category">Category</th>
                <th className="col-deal">Deal</th>
                <th className="col-status">AI Status</th>
                <th className="col-date">Date</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocuments.map((doc, idx) => (
                <tr key={doc.jobId || idx} className={selectedDocs.has(doc.jobId) ? 'selected' : ''}>
                  <td>
                    <button className="btn-checkbox" onClick={() => toggleSelectDoc(doc.jobId)}>
                      {selectedDocs.has(doc.jobId) ? 
                        <CheckSquare size={18} /> : <Square size={18} />
                      }
                    </button>
                  </td>
                  <td>
                    <div className="doc-filename">
                      <FileText size={16} />
                      <span>{doc.filename}</span>
                    </div>
                  </td>
                  <td>
                    <button
                      className={`category-badge ${doc.categorySuggestedByAI ? 'ai-suggested' : 'manual'} category-badge-btn`}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        const id = doc.jobId || idx;
                        if (openCategoryDropdown?.jobId === id) {
                          setOpenCategoryDropdown(null);
                        } else {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setOpenCategoryDropdown({ jobId: id, top: rect.bottom + 4, left: rect.left });
                        }
                      }}
                    >
                      <span className="category-badge-text" title={getSLBCategoryLabel(doc.category) || doc.category || 'General'}>
                        {getSLBCategoryLabel(doc.category) || doc.category || 'General'}
                      </span>
                      {doc.categorySuggestedByAI && <span className="ai-indicator">AI</span>}
                      <span className="category-badge-chevron">▾</span>
                    </button>
                  </td>
                  <td>
                    <div className="deal-cell">
                      {doc.dealId ? (
                        <>
                          <span className="deal-badge">
                            <FolderOpen size={12} /> {doc.dealName}
                          </span>
                          <button 
                            className="btn-unlink"
                            onClick={() => handleLinkDeal(doc.jobId, null)}
                            title="Unlink from deal"
                          >
                            <Unlink size={12} />
                          </button>
                        </>
                      ) : (
                        <select
                          className="deal-select"
                          value=""
                          onChange={(e) => handleLinkDeal(doc.jobId, e.target.value)}
                        >
                          <option value="">Link to deal...</option>
                          {deals.map(deal => (
                            <option key={deal.id} value={deal.id}>{deal.name}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </td>
                  <td>
                    <ProcessingStatusBadge status={doc.aiProcessingStatus || 'pending'} />
                  </td>
                  <td>
                    <span className="doc-date">
                      <Calendar size={12} />
                      {doc.createdAt
                        ? new Date(doc.createdAt).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric'
                          })
                        : '—'
                      }
                    </span>
                  </td>
                  <td>
                    <div className="doc-actions">
                      <button 
                        className="btn-action"
                        onClick={() => onOpenAnnotate?.(doc.jobId)}
                        title="Open & Annotate"
                      >
                        <FileText size={16} />
                      </button>
                      {canEdit && <button
                        className="btn-action btn-delete"
                        onClick={() => handleDeleteDocument(doc.jobId, doc.filename)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Bulk Category Modal */}
      {showCategoryModal && (
        <div className="modal-overlay" onClick={() => setShowCategoryModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Change Category for {selectedDocs.size} Documents</h3>
              <button className="btn-close" onClick={() => setShowCategoryModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <select 
                value={bulkTargetValue} 
                onChange={(e) => setBulkTargetValue(e.target.value)}
                className="bulk-select"
              >
                <option value="">Select category...</option>
                {allCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCategoryModal(false)}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleBulkCategoryUpdate}
                disabled={!bulkTargetValue}
              >
                Update Category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Deal Modal */}
      {showDealModal && (
        <div className="modal-overlay" onClick={() => setShowDealModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Link {selectedDocs.size} Documents to Deal</h3>
              <button className="btn-close" onClick={() => setShowDealModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <select 
                value={bulkTargetValue} 
                onChange={(e) => setBulkTargetValue(e.target.value)}
                className="bulk-select"
              >
                <option value="">Select deal...</option>
                {deals.map(deal => (
                  <option key={deal.id} value={deal.id}>{deal.name}</option>
                ))}
              </select>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDealModal(false)}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleBulkDealLink}
                disabled={!bulkTargetValue}
              >
                Link to Deal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Categories Modal */}
      {showManageCategoriesModal && (
        <div className="modal-overlay" onClick={() => setShowManageCategoriesModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Manage Categories</h3>
              <button className="btn-close" onClick={() => setShowManageCategoriesModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="default-categories">
                <h4>Default Categories</h4>
                <div className="category-list">
                  {defaultCategories.map(cat => (
                    <span key={cat} className="category-tag default">{cat}</span>
                  ))}
                </div>
              </div>
              
              <div className="custom-categories">
                <h4>Custom Categories</h4>
                {customCategories.length === 0 ? (
                  <p className="no-custom-cats">No custom categories yet.</p>
                ) : (
                  <div className="category-list">
                    {customCategories.map(cat => (
                      <span key={cat} className="category-tag custom">
                        {cat}
                        {canEdit && <button
                          className="btn-remove-cat"
                          onClick={() => handleDeleteCategory(cat)}
                        >
                          <X size={12} />
                        </button>}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              {canEdit && <div className="add-category">
                <h4>Add New Category</h4>
                <div className="add-category-input">
                  <input
                    type="text"
                    placeholder="Category name..."
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                  />
                  <button className="btn-primary" onClick={handleAddCategory}>
                    <Plus size={16} /> Add
                  </button>
                </div>
              </div>}
            </div>
          </div>
        </div>
      )}

      {/* Category dropdown — fixed position to escape table stacking context */}
      {openCategoryDropdown && (() => {
        const doc = filteredDocuments.find((d, i) => (d.jobId || i) === openCategoryDropdown.jobId);
        const linkedDeal = doc?.dealId ? deals.find(d => d.id === doc.dealId) : null;
        const isSLB = linkedDeal?.deal_type_id === 'sale_leaseback_industrial';
        return (
          <div
            className="category-dropdown-menu"
            style={{ position: 'fixed', top: openCategoryDropdown.top, left: openCategoryDropdown.left, zIndex: 9999, maxHeight: 360, overflowY: 'auto' }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {isSLB ? (
              <>
                <div style={{ padding: '6px 12px', fontSize: 11, fontWeight: 600, color: '#1E40AF', borderBottom: '1px solid #e5e7eb' }}>Property Track</div>
                {SLB_PROPERTY_CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    className={`category-dropdown-item ${doc?.category === cat.id ? 'active' : ''}`}
                    onClick={() => { handleUpdateCategory(openCategoryDropdown.jobId, cat.id); setOpenCategoryDropdown(null); }}
                  >
                    {cat.label}
                  </button>
                ))}
                <div style={{ padding: '6px 12px', fontSize: 11, fontWeight: 600, color: '#C2410C', borderBottom: '1px solid #e5e7eb', borderTop: '1px solid #e5e7eb' }}>Tenant Credit Track</div>
                {SLB_TENANT_CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    className={`category-dropdown-item ${doc?.category === cat.id ? 'active' : ''}`}
                    onClick={() => { handleUpdateCategory(openCategoryDropdown.jobId, cat.id); setOpenCategoryDropdown(null); }}
                  >
                    {cat.label}
                  </button>
                ))}
                <div style={{ padding: '6px 12px', fontSize: 11, fontWeight: 600, color: '#6B7280', borderBottom: '1px solid #e5e7eb', borderTop: '1px solid #e5e7eb' }}>Standard</div>
                {[...defaultCategories, ...customCategories].map(cat => (
                  <button
                    key={cat}
                    className={`category-dropdown-item ${doc?.category === cat ? 'active' : ''}`}
                    onClick={() => { handleUpdateCategory(openCategoryDropdown.jobId, cat); setOpenCategoryDropdown(null); }}
                  >
                    {cat}
                  </button>
                ))}
              </>
            ) : (
              [...defaultCategories, ...customCategories].map(cat => (
                <button
                  key={cat}
                  className={`category-dropdown-item ${(doc?.category || 'General') === cat ? 'active' : ''}`}
                  onClick={() => { handleUpdateCategory(openCategoryDropdown.jobId, cat); setOpenCategoryDropdown(null); }}
                >
                  {cat}
                </button>
              ))
            )}
          </div>
        );
      })()}
    </div>
  );
}

export default Library;
